/**
 * Per-recipe colours for the meal-plan timeline (PLAN §4.1, docs/DESIGN.md).
 *
 * Eight distinct hues derived from the brand — teal, green and orange first,
 * then rotations — each rendered as a set of four related colours so a step
 * card can be tinted and still carry AA-contrast text:
 *
 *   dot    solid swatch (legend chip, colour dot on a plan card); white text
 *          on it clears 4.5:1, and it clears 3:1 against the card surface
 *   tint   card background
 *   ink    text/badge colour on `tint` (≥ 4.5:1 against it, and against
 *          the cream page background and the card surface)
 *   border 1px border for a tinted card
 *
 * Colours are computed from the hue list so the relationships hold by
 * construction; `planColors.test.ts` checks the contrast ratios.
 */

/** Brand teal, brand green, brand orange, then five rotations that stay distinguishable. */
export const PLAN_COLOR_HUES = [168, 76, 28, 205, 320, 262, 118, 350] as const;

export interface PlanColor {
  /** 0-based index into the palette — matches `colorIndex` from the API. */
  index: number;
  hue: number;
  dot: string;
  tint: string;
  ink: string;
  border: string;
}

type Rgb = readonly [number, number, number];

/** HSL (h in degrees, s/l in percent) → `#rrggbb`. */
export function hslToHex(h: number, s: number, l: number): string {
  const [r, g, b] = hslToRgb(h, s, l);
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

function hslToRgb(h: number, s: number, l: number): Rgb {
  const hue = ((h % 360) + 360) % 360;
  const sat = s / 100;
  const lig = l / 100;
  const c = (1 - Math.abs(2 * lig - 1)) * sat;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = lig - c / 2;
  const [r, g, b] =
    hue < 60
      ? [c, x, 0]
      : hue < 120
        ? [x, c, 0]
        : hue < 180
          ? [0, c, x]
          : hue < 240
            ? [0, x, c]
            : hue < 300
              ? [x, 0, c]
              : [c, 0, x];
  return [Math.round((r + m) * 255), Math.round((g + m) * 255), Math.round((b + m) * 255)] as const;
}

/** `#rgb` / `#rrggbb` → rgb triple. */
export function hexToRgb(hex: string): Rgb {
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ] as const;
}

function relativeLuminance([r, g, b]: Rgb): number {
  const channel = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG 2.1 contrast ratio between two hex colours (1–21). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(hexToRgb(a));
  const lb = relativeLuminance(hexToRgb(b));
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

export const PLAN_COLORS: readonly PlanColor[] = PLAN_COLOR_HUES.map((hue, index) => ({
  index,
  hue,
  dot: hslToHex(hue, 62, 30),
  tint: hslToHex(hue, 62, 92),
  ink: hslToHex(hue, 70, 24),
  border: hslToHex(hue, 42, 76),
}));

/** Palette entry for a `colorIndex`; wraps around and tolerates junk indices. */
export function planColor(colorIndex: number): PlanColor {
  const n = PLAN_COLORS.length;
  const i = Number.isFinite(colorIndex) ? ((Math.trunc(colorIndex) % n) + n) % n : 0;
  return PLAN_COLORS[i] as PlanColor;
}

/** Inline styles for a tinted step/recipe card. */
export function planCardStyle(colorIndex: number) {
  const c = planColor(colorIndex);
  return { backgroundColor: c.tint, borderColor: c.border, color: c.ink };
}
